import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import CircularProgress from '@mui/material/CircularProgress'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import classNames from 'classnames'
import { updateLocationMilestone } from 'modules/practices/actions'
import { SimplifeyeLogoIconSmall, VyneLogoIconSmall } from 'modules/shared/svgIcons'

import { RootState } from '../../../../../appReducer'
import { useAppDispatch } from '../../../../../util/useAppDispatch'
import CopyText from '../../../../shared/CopyText'
import GreenSwitch from '../../../../shared/green-switch/GreenSwitch'
import { useLocationCard } from '../../context/LocationCardContext'
import { selectLocationPaymentStatus } from '../../selectors/locationSelectors'
import {
    DisplayProduct,
    getDisplayProducts,
    isLocationInactive,
    isVyneProductsLoading,
} from '../../services/productService'

import LocationCardPayment from './LocationCardPayment'

interface Props {
    location: Models.PracticeLocation
}

const LocationCard = ({ location }: Props) => {
    const dispatch = useAppDispatch()
    const {
        practiceId,
        hasPayments,
        isLocationEditable,
        canManageSurvey,
        expandedPayments,
        openEditLocation,
        openEditSurvey,
        openLocationProducts,
    } = useLocationCard()

    const [loadingEdit, setLoadingEdit] = useState(false)

    const {
        stripeIDStatus,
        paymentsRatesStatus,
        allFieldsCompleted,
        isTrainingComplete,
    } = useSelector((state: RootState) => selectLocationPaymentStatus(state, practiceId, location.id))

    const displayProducts = getDisplayProducts(location)
    const isInactive = isLocationInactive(location)
    const vyneProductsLoading = isVyneProductsLoading(location)
    const locationHasSurveyWarning = Boolean(location.survey?.status === 'not_completed')
    const isPaymentsCardExpanded = expandedPayments?.id === location.id
    const isPaymentsCardLoading = Boolean(expandedPayments?.isLoading)

    const handleEditSurvey = (): void => openEditSurvey(location)

    const handleEditLocation = async () => {
        setLoadingEdit(true)
        await openEditLocation(location)
        setLoadingEdit(false)
    }

    const handleTrainingSwitch = async () => {
        const willBeChecked = !isTrainingComplete
        await dispatch(updateLocationMilestone(practiceId, location.id, willBeChecked))
    }

    const handleOpenLocationsProductModal = (): void => {
        openLocationProducts(location.id)
    }

    const renderProducts = (): JSX.Element => {
        if (vyneProductsLoading) {
            return <CircularProgress size={20} color="primary" variant="indeterminate" />
        }

        if (displayProducts.length === 0) {
            return (
                <div
                    className={classNames('locations-tab__product', {
                        'locations-tab--not-active': location.isVyneControlled,
                    })}
                >
                    {location.isVyneControlled ? 'No Products' : 'Add Products'}
                </div>
            )
        }

        return (
            <div>
                {displayProducts.map((product: DisplayProduct) => (
                    <div
                        key={product.id}
                        className={classNames('locations-tab__product', {
                            'locations-tab--product-inactive': !product.active,
                            'locations-tab--not-active': location.isVyneControlled,
                        })}
                    >
                        {product.displayName}
                    </div>
                ))}
            </div>
        )
    }

    const name = location.customerId ? location.customerName : location.name
    const regionName = location.regionName

    return (
        <Card className={classNames('card', { 'inactive-card': isInactive })}>
            <div className="header">
                {name}
                <div className="vyne-controlled-icon">
                    {location.isVyneControlled ? <VyneLogoIconSmall /> : <SimplifeyeLogoIconSmall />}
                </div>
            </div>
            <CardContent className="address">
                {location.address && (
                    <div className="locations-tab__address-wrapper">
                        <div className="label">Address</div>
                        <div style={{ paddingRight: '12px' }}>{location.address.street}</div>
                        <div style={{ paddingRight: '12px' }}>{location.address.unit}</div>
                        <div
                            style={{ paddingRight: '12px' }}
                        >{`${location.address.city}, ${location.address.state} ${location.address.zip}`}</div>
                    </div>
                )}

                {location.customerId && (
                    <span className="locations-tab__copy-id id">
                        Practice (Customer) ID: <CopyText text={location.customerId!}>{location.customerId}</CopyText>
                    </span>
                )}
                <div>
                    <span className="locations-tab__copy-id id">
                        Simplifeye Location ID: <CopyText text={location.id}>{location.id}</CopyText>
                    </span>
                </div>

                {regionName && <span className="locations-tab__copy-id id">Region Name: {regionName}</span>}

                <div className="locations-tab__facility-id">
                    {location.facilityId ? (
                        <span className="locations-tab__copy-facility-id id">
                            Facility ID: <CopyText text={location.facilityId}>{location.facilityId}</CopyText>
                        </span>
                    ) : (
                        <span>Facility ID: N/A</span>
                    )}
                </div>
                <div
                    className={classNames('locations-tab__products', {
                        'locations-tab--not-clickable': location.isVyneControlled,
                        'locations-tab__products-without-payments': !hasPayments,
                    })}
                    onClick={location.isVyneControlled ? undefined : handleOpenLocationsProductModal}
                >
                    {renderProducts()}
                </div>

                {hasPayments && (
                    <LocationCardPayment
                        locationId={location.id}
                        locationName={location.name}
                        paymentsInfo={location.paymentsInfo}
                        isPaymentsCardLoading={isPaymentsCardLoading}
                        isPaymentsCardExpanded={isPaymentsCardExpanded}
                        stripeConnectedAccountId={location.stripeConnectedAccountId}
                        stripeLocationId={location.stripeLocationId}
                        statementDescriptor={location.statementDescriptor}
                        allFieldsCompleted={allFieldsCompleted}
                        stripeIDStatus={stripeIDStatus}
                        paymentsRatesStatus={paymentsRatesStatus}
                    />
                )}
            </CardContent>
            <CardActions>
                <div className="buttons-wrapper-one-row">
                    <div className="buttons">
                        <Button
                            className="button"
                            size="small"
                            disabled={!isLocationEditable}
                            onClick={handleTrainingSwitch}
                        >
                            <div>
                                <div>
                                    <GreenSwitch
                                        className="locations-tab__training-slider"
                                        checked={isTrainingComplete ?? false}
                                    />
                                </div>
                                <div>Training</div>
                            </div>
                        </Button>

                        {canManageSurvey && location.survey?.id && (
                            <Button className="button" size="small" onClick={handleEditSurvey}>
                                {locationHasSurveyWarning ? (
                                    <div className="locations-tab__survey-error-container">
                                        <div>
                                            <i className="material-icons locations-tab__survey-error-icon">error</i>
                                        </div>
                                        <div>Survey</div>
                                    </div>
                                ) : (
                                    'Survey'
                                )}
                            </Button>
                        )}

                        <Button
                            className="button"
                            size="small"
                            onClick={handleEditLocation}
                            disabled={!isLocationEditable}
                        >
                            Edit
                            {loadingEdit && (
                                <div className="locations-tab__small-loader">
                                    <CircularProgress size={10} />
                                </div>
                            )}
                        </Button>
                    </div>
                </div>
            </CardActions>
        </Card>
    )
}

export default LocationCard
